import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './navigation-menu.scss?inline'
import DapDSNavigationMenuItem from './navigation-menu-item.component'

/**
 * `dap-ds-navigation-menu`
 * @summary A horizontal navigation menu container that can contain multiple navigation items.
 * @element dap-ds-navigation-menu
 * @title - Navigation Menu
 *
 * @property {string} activeHref - The currently active href for highlighting active navigation items.
 * @property {'horizontal' | 'vertical'} orientation - The orientation of the navigation menu.
 *
 * @event {{ href: string, event: Event }} dds-navigation-item-click - Fired when a navigation item is clicked.
 *
 * @slot - The navigation menu list and items.
 *
 * @csspart base - The main navigation menu container.
 */
@customElement('dap-ds-navigation-menu')
export default class DapDSNavigationMenu extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  /** The currently active href for highlighting active navigation items. */
  @property({ type: String }) activeHref?: string

  /** The orientation of the navigation menu.
   * @type {'horizontal' | 'vertical'}
   */
  @property({ reflect: true }) orientation: 'horizontal' | 'vertical' =
    'horizontal'

  /** Whether the navigation menu should take full width of the screen. */
  @property({ type: Boolean }) fullWidth = false

  connectedCallback() {
    super.connectedCallback()
    if (!this.activeHref) {
      this.activeHref = window.location.pathname
    }

    this.addEventListener(
      'dds-navigation-item-click',
      this._handleNavigationItemClick,
    )

    // Listen for dropdown open events to close others
    this.addEventListener(
      'dds-navigation-dropdown-open',
      this._handleDropdownOpen,
    )
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    if (changedProperties.has('activeHref')) {
      this._updateActiveHref()
    }

    if (changedProperties.has('fullWidth')) {
      this._updateChildFullWidth()
    }
  }

  firstUpdated(changedProperties: Map<string, unknown>) {
    super.firstUpdated(changedProperties)
    this._updateChildFullWidth()
  }

  private _updateChildFullWidth() {
    const navigationItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    navigationItems.forEach(item => {
      ;(item as DapDSNavigationMenuItem).fullWidth = this.fullWidth
    })
  }

  private _updateActiveHref() {
    const navigationItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    navigationItems.forEach(item => {
      ;(item as DapDSNavigationMenuItem).activeHref = this.activeHref
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      'dds-navigation-item-click',
      this._handleNavigationItemClick,
    )
    this.removeEventListener(
      'dds-navigation-dropdown-open',
      this._handleDropdownOpen,
    )
  }

  private _handleNavigationItemClick = (event: Event) => {
    const customEvent = event as CustomEvent
    // Re-emit the event to parent components if needed
    this.emit('dds-navigation-item-click', customEvent.detail)
  }

  private _handleDropdownOpen = (event: Event) => {
    const openingItem = event.target as DapDSNavigationMenuItem

    const allItems = this.querySelectorAll('dap-ds-navigation-menu-item')
    allItems.forEach(item => {
      if (item !== openingItem && (item as DapDSNavigationMenuItem).opened) {
        ;(item as DapDSNavigationMenuItem).hideDropDown()
      }
    })
  }

  render() {
    return html`
      <nav
        part="base"
        class=${clsx('navigation-menu', {
          'navigation-menu--vertical': this.orientation === 'vertical',
        })}
        role="navigation"
        aria-label=${ifDefined(this.ariaLabel || '')}>
        <slot></slot>
      </nav>
    `
  }
}
